import { defineAsyncComponent, type FunctionalComponent, type SVGAttributes } from 'vue'
import { statusColors } from '@/plugins/vuetify'

/**
 * Takes a string and returns a color based on the string's content
 * @param text string to convert to color
 * @returns color name from the vuetify theme
 */
export function textToBackgroundColor(text: string): string {
  const colors = Object.keys(statusColors)
  let charSum = 0
  for (let idx = 0; idx < text.length; idx++) {
    charSum += text.charCodeAt(idx)
  }
  return colors[charSum % colors.length] ?? 'primary'
}

/**
 * Takes a string and returns the initials of the string, handles emails, names
 * with multiple parts, etc.
 * @param _text string to convert to initials
 * @returns initials of the string
 */
export function textInitials(_text: string): string {
  const text = _text.includes('@') ? (_text.split('@')[0] ?? _text.trim()) : _text.trim()

  // TODO: use Intl.Segmenter?
  let separator = ' '
  for (const sep of ['-', '_', '.']) {
    if (text.includes(sep)) {
      separator = sep
      break
    }
  }

  const parts = text.split(separator)

  let initials = parts[0]?.charAt(0) ?? ''
  if (parts.length > 1) initials += parts[1]?.charAt(0) ?? ''

  return initials.toUpperCase()
}

/**
 * Takes a MIME type and returns a icon component that can be used with
 * v-icon
 * @param mimeType MIME type to convert to icon
 * @returns icon component
 */
export function mimeTypeToIcon(mimeType?: string): FunctionalComponent<SVGAttributes> {
  switch (mimeType?.split('/').at(-1)) {
    case 'json':
      return defineAsyncComponent(() => import('~icons/mdi/code-json'))
    case 'csv':
      return defineAsyncComponent(() => import('~icons/mdi/file-delimited'))
    case 'html':
      return defineAsyncComponent(() => import('~icons/mdi/language-html5'))
    case 'jpeg':
      return defineAsyncComponent(() => import('~icons/mdi/file-jpg-box'))
    case 'png':
      return defineAsyncComponent(() => import('~icons/mdi/file-png-box'))
    case 'svg':
      return defineAsyncComponent(() => import('~icons/mdi/svg'))
    case 'x-bzip':
    case 'x-bzip2':
      return defineAsyncComponent(() => import('~icons/mdi/zip-box'))
    case 'pdf':
      return defineAsyncComponent(() => import('~icons/mdi/file-pdf-box'))
    case 'markdown':
      return defineAsyncComponent(() => import('~icons/mdi/language-markdown'))
    case 'plain':
      return defineAsyncComponent(() => import('~icons/mdi/format-text'))
    default:
      return defineAsyncComponent(() => import('~icons/mdi/file-document-outline'))
  }
}

/**
 * Map of MIME type subtypes to file information.
 * The subtype is the part after the slash in a MIME type (e.g., 'pdf' from 'application/pdf').
 */
const mimeSubtypeToFileInfo: Record<string, { extension: string; description: string }> = {
  'pdf': { extension: '.pdf', description: 'PDF' },
  'plain': { extension: '.txt', description: 'Text' },
  'html': { extension: '.html', description: 'HTML' },
  'markdown': { extension: '.md', description: 'Markdown' },
  'json': { extension: '.json', description: 'JSON' },
  'csv': { extension: '.csv', description: 'CSV' },
  'xml': { extension: '.xml', description: 'XML' },
  'msword': { extension: '.doc', description: 'Word' },
  'vnd.openxmlformats-officedocument.wordprocessingml.document': { extension: '.docx', description: 'Word' },
  'vnd.ms-excel': { extension: '.xls', description: 'Excel' },
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet': { extension: '.xlsx', description: 'Excel' },
  'vnd.ms-powerpoint': { extension: '.ppt', description: 'PowerPoint' },
  'vnd.openxmlformats-officedocument.presentationml.presentation': { extension: '.pptx', description: 'PowerPoint' },
  'jpeg': { extension: '.jpg', description: 'JPEG' },
  'png': { extension: '.png', description: 'PNG' },
  'gif': { extension: '.gif', description: 'GIF' },
  'bmp': { extension: '.bmp', description: 'BMP' },
  'webp': { extension: '.webp', description: 'WebP' },
  'svg+xml': { extension: '.svg', description: 'SVG' },
  'svg': { extension: '.svg', description: 'SVG' },
  'tiff': { extension: '.tiff', description: 'TIFF' },
  'zip': { extension: '.zip', description: 'ZIP' },
  'x-rar-compressed': { extension: '.rar', description: 'RAR' },
  'x-7z-compressed': { extension: '.7z', description: '7-Zip' },
  'x-tar': { extension: '.tar', description: 'TAR' },
  'gzip': { extension: '.gz', description: 'GZIP' },
  'x-bzip': { extension: '.bz', description: 'BZIP' },
  'x-bzip2': { extension: '.bz2', description: 'BZIP2' },
  'octet-stream': { extension: '', description: 'Binary' },
  'rtf': { extension: '.rtf', description: 'RTF' },
}

/**
 * Convert a MIME type to a user-friendly file type name.
 * @param mimeType The MIME type to convert (e.g., 'application/pdf')
 * @param format The output format: 'extension' for '.pdf', 'description' for 'PDF', 'full' for 'PDF (.pdf)'
 * @returns A user-friendly file type name, or the MIME subtype as fallback
 *
 * @example
 * formatMimeType('image/jpeg') // 'JPEG (.jpg)'
 * formatMimeType('application/pdf') // 'PDF (.pdf)'
 * formatMimeType('application/pdf', 'extension') // '.pdf'
 * formatMimeType('application/pdf', 'description') // 'PDF'
 */
export function mimeTypeToName(
  mimeType: string | undefined | null,
  format: 'extension' | 'description' | 'full' = 'full'
): string {
  if (mimeType == null || mimeType === '') return '-'

  const subtype = mimeType.split('/').at(-1) ?? ''
  const info = mimeSubtypeToFileInfo[subtype]

  if (info == null) return subtype

  switch (format) {
    case 'description':
      return info.description
    case 'full':
      return info.extension !== '' ? `${info.description} (${info.extension})` : info.description
    case 'extension':
    default:
      return info.extension !== '' ? info.extension : info.description
  }
}

const regexSpecialCharacters = /[.^$*+\-?()[\]{}\\|—/]/g
export function sanitizeRegex(str: string) {
  return str.replace(regexSpecialCharacters, '\\$&')
}
